/**
 * The visit page's form parts — one set for every sheet and popup (lead, Oct
 * 29): a field's label with the green required dot or *optional* (Boards 4,
 * 18 B), the 36 px box a field sits in (Board 3 B), the boards' radio and tick
 * box, the green choice chip, and `fill` — a sentence from `copy.ts` with a
 * piece of it drawn differently (a PO in green, a figure in bold).
 *
 * Measured from the boards' HTML; colours from the tokens; no words of its own.
 */
import { Fragment, type ReactNode } from 'react'
import { cn } from '../lib/cn.js'
import { VisitGlyph } from './glyphs.js'

/**
 * Where a part of a sentence is drawn differently (a PO in green, a figure in
 * bold), the copy function is called with this in that part's place and the
 * sentence is split around it — so the words stay `copy.ts`'s, whole.
 */
export const SLOT = '\u0000'

/** The sentence with each `SLOT` replaced, in order, by the nodes given. */
export function fill(sentence: string, ...nodes: ReactNode[]): ReactNode {
  const pieces = sentence.split(SLOT)
  return pieces.map((piece, index) => (
    <Fragment key={index}>
      {piece}
      {index < pieces.length - 1 ? nodes[index] : null}
    </Fragment>
  ))
}

/** A radio the boards draw: a 5 px green ring when chosen, a grey outline when not. */
export function RadioDot({ on, size }: { on: boolean; size: 16 | 17 | 18 }) {
  return (
    <span
      aria-hidden="true"
      className={cn(
        'inline-block shrink-0 rounded-full bg-surface',
        on ? 'border-[5px] border-accent' : 'border-[1.5px] border-line-strong',
      )}
      style={{ width: size, height: size }}
    />
  )
}

/** The 18 px tick box (Board 4: *Ask Tom Reyes …*, *Tell the crew*). */
export function TickBox({ on }: { on: boolean }) {
  return (
    <span
      aria-hidden="true"
      className={cn(
        'inline-flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-[5px]',
        on ? 'bg-accent text-white' : 'border-[1.5px] border-line-strong bg-surface',
      )}
    >
      {on && <VisitGlyph name="check" size={12} stroke={3} />}
    </span>
  )
}

/**
 * A choice chip (Board 4's *Why*, Board 3 B's *How it gets there*, Board 6 B's
 * *Days*) — green with a 12 px tick when chosen, never black. One of several
 * by default (a radio); `multi` when several may be chosen at once (a tick
 * box's role — Board 6 B's days).
 */
export function ChoiceChip({ on, onClick, children, size, multi = false }: {
  on: boolean
  onClick: () => void
  children: ReactNode
  /** 28 / 12 (Board 4), 30 / 12.5 (Board 3 B, 6 B), or 44 / 12.5 — the 30 chip on a phone, at §19.2's touch target. */
  size: 28 | 30 | 44
  multi?: boolean
}) {
  return (
    <button
      type="button"
      role={multi ? 'checkbox' : 'radio'}
      aria-checked={on}
      onClick={onClick}
      className={cn(
        'inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border font-semibold',
        size === 28 ? 'h-7 px-2.5 text-[12px]' : size === 30 ? 'h-[30px] px-3 text-[12.5px]' : 'h-11 px-3 text-[12.5px]',
        on ? 'border-accent-line bg-accent-soft text-accent-dark' : 'border-line bg-surface text-text hover:bg-hover',
      )}
    >
      {on && <VisitGlyph name="check" size={12} stroke={2.6} />}
      {children}
    </button>
  )
}

/** A field's label — 11.5 muted, the green required dot (Board 4), *optional* on the right (Board 18 B). */
export function FieldLabel({ children, required = false, optional, htmlFor }: {
  children: ReactNode
  required?: boolean
  optional?: string
  htmlFor?: string
}) {
  return (
    <label htmlFor={htmlFor} className="mb-[5px] flex items-center gap-1 text-[11.5px] font-medium text-text-muted">
      {children}
      {required && <i aria-hidden="true" className="inline-block h-[5px] w-[5px] rounded-full bg-accent" />}
      {optional && <span className="ml-auto text-text-faint">{optional}</span>}
    </label>
  )
}

/** The 36 px box a field sits in (Board 3 B: radius 8, 11 either side, 13 px). */
export const FIELD_BOX = 'flex h-9 w-full items-center rounded-button border border-line bg-surface px-[11px] text-[13px] '
  + 'text-text focus-within:border-accent focus-within:shadow-[0_0_0_3px_rgb(var(--color-accent-ring))]'

/**
 * The input inside `FIELD_BOX`. The box draws the focus ring; the input
 * cancels tokens.css's global `:focus-visible` ring so there is never a ring
 * inside the ring.
 */
export const FIELD_INPUT = 'min-w-0 flex-1 bg-transparent text-left outline-none placeholder:text-text-faint '
  + 'focus-visible:rounded-none focus-visible:shadow-none'
